import { Component, OnInit, OnDestroy, Injector, ViewChild, ViewContainerRef, TemplateRef } from '@angular/core';
import { Subscription } from 'rxjs';

import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

// components
import { FullListComponent } from '../../../shared/components/full-list/full-list.component';
import { DistributionNumberComponent } from './distribution-number.component';

// models
import { DistributionNumber } from '../../../shared/models/distribution-number';

// services
import { DistributionManagementService } from '../../shared/services/distribution-management.service';
import { MasterDataService } from '../../shared/services/master-data.service';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { AuctionClusterBuyerService } from '../../shared/services/auction-cluster-buyer.service';

import { one } from "devextreme/events";



@Component({
  selector: 'distribution-numbers-component',
  templateUrl: 'distribution-numbers.component.html',
  styleUrls: ['./distribution-numbers.component.scss']
})
export class DistributionNumbersComponent extends FullListComponent<DistributionNumber, DistributionNumberComponent> implements OnInit, OnDestroy {

  @ViewChild('confirmation') confirmation: any;
  @ViewChild('details') detailsComponent: DistributionNumberComponent;
  @ViewChild('distributionNumbersGrid') dataGridInstance: DxDataGridComponent;

  @ViewChild("filterRowEditor", { static: true })
  filterRowEditorRef: TemplateRef<any>;


  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;
  distributionNumberDataSource = {};
  masterDataLookupSource = {};
  buyerCustomStore: CustomStore;
  buyerDataSource: DataSource;
  buyerCachedlookupStore: CustomStore;
  buyerLookupCache = new Map<number, any>();

  isFixedNumbers: boolean = false;
  editingEnabled = true;

  constructor(
    protected injector: Injector,
    private distributionManagementService: DistributionManagementService,
    private masterDataService: MasterDataService,
    private buyerService: AuctionClusterBuyerService,
    private translateService: TranslateService,
    private viewContainerRef: ViewContainerRef
  ) {
    super(injector, DistributionNumber);
    this._subscription = this.language.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });

    this.isFixedNumbers = this.route.snapshot.routeConfig.data['isFixed'];

    this.buyerCustomStore = new CustomStore({
      key: 'buyerId',
      load: async (loadOptions) => {

        if (loadOptions.searchValue) {
          loadOptions.filter = ["buyerDisplayValue", "contains", loadOptions.searchValue];
        }
        var result = await this.buyerService.getBuyersForDropDownPaged(this.id, loadOptions).toPromise();
        return result;
      },
      byKey: async (key) => {
        var result = await this.buyerService.getBuyerForDropDown(this.id, key).toPromise();
        return result;
      }
    }
    );

    this.buyerDataSource = new DataSource({
      paginate: true,
      pageSize: 20,
      requireTotalCount: false,
      store: this.buyerCustomStore
    });


    this.buyerCachedlookupStore = new CustomStore({
      key: 'buyerId',

      load: () => {
        var result = Array.from(this.buyerLookupCache.values());
        return result;
      },

      byKey: (key) => {
        if (this.buyerLookupCache.has(key)) {
          return Promise.resolve(this.buyerLookupCache.get(key));
        }

        return this.buyerService.getBuyerForDropDown(this.id, key).toPromise().then(item => {
          this.buyerLookupCache.set(key, item);
          return item;
        });
      }
    });

    this.masterDataLookupSource = {
      store: new CustomStore({
        key: 'masterDataListRowId',
        load: (loadOptions) => {

          if (loadOptions.searchValue) {
            loadOptions.filter = ["label", "contains", loadOptions.searchValue];
          }

          return this.masterDataService.getSysMasterDataListForDropdown(15/*DistributionCategory*/,
            this.id, this.translateService.currentLang, null, loadOptions).toPromise();
        },
        byKey: async (key) => {
          var result = await this.masterDataService.getSysMasterDataListForDropdown(15/*DistributionCategory*/,
            this.id, this.translateService.currentLang, key, null).toPromise();
          return result.data[0];
        }
      }
      )
    };

    this.distributionNumberDataSource = new DataSource({
      paginate: true,
      requireTotalCount: true,
      store: new CustomStore({
        key: "distributionNumberId",
        load: async (loadOptions) => {
          var result = await this.distributionManagementService.getDistributionNumbersPaged(this.id, loadOptions, this.isFixedNumbers).toPromise();
          return {
            data: result.data,
            totalCount: result.totalCount,
          };
        },
      }),

    });
  }

  onEditorPreparing = (e: any) => {
    if (e.name === "buyerName" && e.parentType === "filterRow") {
      e.cancel = true;
      const childView = this.viewContainerRef.createEmbeddedView(
        this.filterRowEditorRef,
        { options: e }
      );
      childView.rootNodes.forEach((element) => {
        e.editorElement.appendChild(element);
      });

      one(e.editorElement, "dxremove", () => {
        childView.destroy();
      });
    }
  }


  ngOnInit() {

    if (this.isFixedNumbers) {
      this.title.set('DISTRIBUTION_MANAGEMENT.DISTRIBUTION_NUMBERS_FIXED_TITLE');
    } else {
      this.title.set('DISTRIBUTION_MANAGEMENT.DISTRIBUTION_NUMBERS_VARIABLE_TITLE');
    }
    
    this.setTranslations('DISTRIBUTION_MANAGEMENT');

    this.distributionManagementService.checkDeletePossible(this.id)
      .subscribe(_ => {
        this.editingEnabled = true;
      },
        error => {
          this.editingEnabled = false;
          this.spinner.hide();
        });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  edit = (e: any) => {
    if (!this.isFixedNumbers) {
      return;
    }
    if(!this.editingEnabled)
    {
      return;
    }
    const distributionRangeId = e.row !== undefined ? e.row.data.distributionNumberId : e.data.distributionNumberId;
    this.detailsComponent.modalTitle = this.translations.EDIT;
    this.detailsComponent.open(distributionRangeId, this.id, this.isFixedNumbers);
  }

  add() {
    if (!this.isFixedNumbers) {
      return;
    }
    this.detailsComponent.modalTitle = this.translations.ADD_NEW;
    this.detailsComponent.open(null, this.id, this.isFixedNumbers);
  }

  deleteItem = (e: any) => {
    if (!this.isFixedNumbers) {
      return;
    }

    this.distributionManagementService.checkDeletePossible(this.id)
      .subscribe(_ => {
       this.itemIdToDelete = e.row.data.distributionNumberId;
       this.confirmation.opened = true;
      },
        error => {
          this.errorService.show(error);
          this.spinner.hide();
        });
    
  }

  deleteSelected() {
    this.spinner.show();
    this.distributionManagementService.deleteDistributionNumber(this.id, this.itemIdToDelete)
      .subscribe(_ => {
        this.dataGridInstance.instance.refresh();
        this.spinner.hide();
      },
        error => {
          this.errorService.show(error);
            this.spinner.hide();
        });
  }

  onDetailsComponentClosed(isDataChanged: boolean) {
    if (isDataChanged) {
      this.dataGridInstance.instance.refresh();
    }
  }
}
